<template>
  <ContentCard :loading="loading" :progress="uploadProgress">
    <template #header>
      <slot name="tabs" />
    </template>
    <template #subheader>
      <ContentCardSubheader :back-to="backTo">
        {{ t('Reviews.edit.assetsTitle') }}
        <template #actions>
          <button class="btn-secondary-color-s mr-2" @click="onCancel">
            {{ t('Reviews.edit.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" :disabled="submitDisabled" data-testid="save-button" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="py-6">
      <FencingBanner
        v-if="documentsFenced"
        data-testid="documents-fenced-banner"
        :headline="t('Reviews.product.view.media.banner.documentsFencingHeadline')"
        :feature-list="fencedFeatureList"
        :cta-label="t('Reviews.product.view.media.banner.documentsFencingCtaLabel')"
        :cta-link="t('Reviews.product.view.media.banner.documentsFencingCtaLink')"
        :banner-image="documentsFencingImageUrl"
        placement="banner"
        :entity-type="entityType"
        :entity-id="entityId"
      />
      <FeatureFencingWrapper
        data-testid="screenshots-fenced-wrapper"
        :fenced="screenshotsFenced"
        :label="t('Reviews.product.view.assetsEdit.screenshotsFencingLabel')"
        :help-link="t(`Reviews.${subject}.view.assetsEdit.screenshotsFencingHelpLink`)"
        :help-tooltip="t(`Reviews.${subject}.view.assetsEdit.screenshotsFencingTooltip`)"
        :upgrade-cta="t('Reviews.product.view.assetsEdit.screenshotsFencingUpgradeCta')"
        :upgrade-cta-link="t('Reviews.product.view.assetsEdit.screenshotsFencingUpgradeCtaLink')"
        placement="screenshots"
        :entity-type="entityType"
        :entity-id="entityId"
      >
        <div v-if="screenshotsFenced" class="grid grid-cols-3 gap-6 py-2" data-testid="screenshots-fenced-placeholder">
          <div
            v-for="i in 3"
            :key="i"
            class="flex aspect-video items-center justify-center rounded-sm border border-disabled bg-surface-elevation-2"
          >
            <ImageIcon class="lucide-icon-l text-icon-main opacity-40" />
          </div>
        </div>
        <LabelValue
          v-else
          :label="t(`Reviews.${subject}.view.screenshots.label`).toString()"
          show-icon
          :help-tooltip="t('Reviews.product.view.assetsEdit.screenshotsHelp')"
          :help-link="t('Reviews.product.view.assetsEdit.screenshotsHelpLink')"
        >
          <template #hint>
            <div>
              <p
                v-html="
                  t(`Reviews.${subject}.edit.screenshots.uploadHint`, {
                    maxNumberOfFiles: screenshotsLimit,
                  })
                "
              />
            </div>
          </template>
          <ReviewsScreenshotsEdit
            :screenshots="screenshots || []"
            :subject="subject"
            :entity-id="entityId"
            :max-screenshots="screenshotsLimit"
            :upsell-on-limit="hasScreenshotsLimitedByPlan"
            :upsell-cta-label="t('Reviews.product.edit.screenshots.limitReachedUpsell.upgradeCta')"
            :upsell-cta-link="t('Reviews.product.edit.screenshots.limitReachedUpsell.upgradeCtaLink')"
            @change="handleScreenshotsUpdate"
          />
        </LabelValue>
      </FeatureFencingWrapper>
      <FeatureFencingWrapper
        data-testid="documents-fenced-wrapper"
        :fenced="documentsFenced"
        :label="t('Reviews.product.view.assetsEdit.documentsFencingLabel')"
        :help-link="t(`Reviews.${subject}.view.assetsEdit.documentsFencingHelpLink`)"
        :help-tooltip="t(`Reviews.${subject}.view.assetsEdit.documentsFencingTooltip`)"
        :upgrade-cta="t('Reviews.product.view.assetsEdit.documentsFencingUpgradeCta')"
        :upgrade-cta-link="t('Reviews.product.view.assetsEdit.documentsFencingUpgradeCtaLink')"
        placement="documents"
        :entity-type="entityType"
        :entity-id="entityId"
      >
        <div v-if="documentsFenced" class="grid grid-cols-4 gap-6">
          <DocumentCard
            v-for="doc in dummyDocuments"
            :key="doc.id"
            :content-type="doc.contentType"
            :name="doc.name"
            :size="doc.size"
            :preview-url="undefined"
            disabled
          />
          <div />
          <div>
            <button class="btn-secondary-neutral-s" disabled>
              <PlusCircleIcon class="mr-1 lucide-icon-m shrink-0 text-icon-disabled" />
              {{ t('Reviews.product.edit.documents.actions.add', 0) }}
            </button>
          </div>
        </div>
        <LabelValue
          v-else
          :label="t(`Reviews.${subject}.view.documents.label`).toString()"
          show-icon
          :help-tooltip="t('Reviews.product.view.assetsEdit.documentsHelp')"
          :help-link="t('Reviews.product.view.assetsEdit.documentsHelpLink')"
        >
          <template #hint>
            <div>
              <p>
                {{
                  t(`Reviews.${subject}.edit.documents.uploadHint`, {
                    maxNumberOfFiles: MAX_NUMBER_OF_DOCUMENTS,
                  })
                }}
              </p>
              <p>{{ t(`Reviews.${subject}.edit.documents.docsHint`) }}</p>
            </div>
          </template>
          <ReviewsDocumentsEditForm
            :documents="documents"
            :subject="subject"
            :entity-id="entityId"
            :fenced="documentsFenced"
            @change="handleDocumentsUpdate"
          />
        </LabelValue>
      </FeatureFencingWrapper>
    </div>
    <template #footer>
      <div class="flex flex-row items-center justify-end">
        <div>
          <button class="btn-secondary-color-s mr-2" @click="onCancel">
            {{ t('Reviews.edit.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" :disabled="submitDisabled" @click="onSubmit">{{ t('Reviews.edit.action.save') }}</button>
        </div>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>

<script setup lang="ts">
import { ImageIcon, PlusCircleIcon } from '@ramp106/omrjs-core-ui/icons'
import { DocumentCard } from '@ramp106/omrjs-reviews-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import documentsFencingImageUrl from '@/components/ReviewsCommon/Fencing/documents_fencing.png'
import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import FencingBanner from '@/components/ReviewsCommon/Fencing/FencingBanner.vue'
import { splitDocumentsForMutations } from '@/components/ReviewsCommon/ReviewsAssetsEdit/documentsEditHelper.ts'
import type { ReviewsEntityDocumentEditData } from '@/components/ReviewsManagement'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import {
  type ReviewsDocuments,
  type ReviewsScreenshots,
  useCreateReviewsEntityDocumentMutation,
  useDeleteReviewsEntityDocumentsMutation,
  useUpdateReviewsEntityDocumentsMutation,
  useCreateReviewsEntityScreenshotMutation,
  useUpdateReviewsEntityScreenshotsMutation,
  useDeleteReviewsEntityScreenshotsMutation,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper.ts'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import LabelValue from '@/ui/LabelValue.vue'

import { MAX_NUMBER_OF_DOCUMENTS, MAX_NUMBER_OF_SCREENSHOTS, MAX_NUMBER_OF_SCREENSHOTS_FREE_PLAN } from './constants.ts'
import ReviewsDocumentsEditForm from './ReviewsDocumentsEditForm.vue'
import ReviewsScreenshotsEdit from './ReviewsScreenshotsEdit.vue'
import { splitScreenshotsForMutations } from './screenshotsEditHelper.ts'
import type { ReviewsEntityScreenshotEditData } from '../../ReviewsManagement/types.ts'

const props = defineProps<{
  subject: 'agency' | 'product'
  entityId: string
  globalId: string
  backTo: RouteLocationRaw
  documents: ReviewsDocuments
  screenshots: ReviewsScreenshots
  documentsFenced?: boolean
  screenshotsFenced?: boolean
  hasScreenshotsLimitedByPlan?: boolean
}>()

const emit = defineEmits(['update'])

const { t } = useI18n()
const router = useRouter()
const v$ = useVuelidate({}, {})

const uploadProgress = ref(0)

const confirmCloseModal = useTemplateRef('confirmCloseModal')

const fencedFeatureList = computed(() => [
  t('Reviews.product.view.media.banner.documentsFencingItem1'),
  t('Reviews.product.view.media.banner.documentsFencingItem2'),
  t('Reviews.product.view.media.banner.documentsFencingItem3'),
])

const screenshotsLimit = computed(() =>
  props.hasScreenshotsLimitedByPlan ? MAX_NUMBER_OF_SCREENSHOTS_FREE_PLAN : MAX_NUMBER_OF_SCREENSHOTS,
)

const dummyDocuments = [
  {
    id: '1',
    contentType: 'application/pdf',
    name: 'PDF 1',
    size: 6400,
  },
  {
    id: '2',
    contentType: 'application/pdf',
    name: 'PDF 2',
    size: 6400,
  },
  {
    id: '3',
    contentType: 'application/pdf',
    name: 'PDF 3',
    size: 6400,
  },
]

const {
  mutate: mutateUpdateDocuments,
  loading: loadingUpdateDocuments,
  onError: onErrorUpdateDocuments,
} = useUpdateReviewsEntityDocumentsMutation({
  clientId: 'reviews',
})

const {
  mutate: mutateCreateDocument,
  loading: loadingCreateDocument,
  onError: onErrorCreateDocument,
} = useCreateReviewsEntityDocumentMutation({
  clientId: 'reviews',
  context: {
    fetchOptions: {
      useUpload: true,
      onProgress: (progress: ProgressEvent) => {
        uploadProgress.value = Math.round((progress.loaded / progress.total) * 100)
      },
    },
  },
})

const {
  mutate: mutateDeleteDocuments,
  loading: loadingDeleteDocuments,
  onError: onErrorDeleteDocuments,
} = useDeleteReviewsEntityDocumentsMutation({
  clientId: 'reviews',
})

const {
  mutate: mutateUpdateScreenshots,
  loading: loadingUpdateScreenshots,
  onError: onErrorUpdateScreenshots,
} = useUpdateReviewsEntityScreenshotsMutation({
  clientId: 'reviews',
})

const {
  mutate: mutateCreateScreenshot,
  loading: loadingCreateScreenshot,
  onError: onErrorCreateScreenshot,
} = useCreateReviewsEntityScreenshotMutation({
  clientId: 'reviews',
  context: {
    fetchOptions: {
      useUpload: true,
      onProgress: (progress: ProgressEvent) => {
        uploadProgress.value = Math.round((progress.loaded / progress.total) * 100)
      },
    },
  },
})

const {
  mutate: mutateDeleteScreenshots,
  loading: loadingDeleteScreenshots,
  onError: onErrorDeleteScreenshots,
} = useDeleteReviewsEntityScreenshotsMutation({
  clientId: 'reviews',
})

const loading = computed(
  () =>
    loadingUpdateScreenshots.value ||
    loadingCreateScreenshot.value ||
    loadingDeleteScreenshots.value ||
    loadingUpdateDocuments.value ||
    loadingCreateDocument.value ||
    loadingDeleteDocuments.value,
)

const submitDisabled = computed(() => loading.value || v$.value.$invalid)

const editableDocuments = ref<ReviewsEntityDocumentEditData[]>([])

const handleDocumentsUpdate = (updatedDocuments: ReviewsEntityDocumentEditData[]) => {
  editableDocuments.value = updatedDocuments
}

const editableScreenshots = ref<ReviewsEntityScreenshotEditData[]>([])

const handleScreenshotsUpdate = (updatedScreenshots: ReviewsEntityScreenshotEditData[]) => {
  editableScreenshots.value = updatedScreenshots
}

function updateDocumentsPosition(documentsToUpdate: ReviewsEntityDocumentEditData[]) {
  let positionCounter = 0
  documentsToUpdate.forEach((document) => {
    if (!document.deleted) {
      positionCounter += 1
      document.position = positionCounter
    }
  })
  return documentsToUpdate
}

const entityType = props.subject === 'product' ? 'tool' : 'service'

function onCancel() {
  track('profile_update_cancel', { surface: 'editor', entity_type: entityType, entity_id: props.entityId })
  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(props.backTo)
  }
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: entityType, entity_id: props.entityId })
  v$.value.$touch()
  if (v$.value.$invalid) {
    showNotification(t(`Reviews.${props.subject}.notifications.invalidData`), 'error')
    return
  }
  const { documentsToCreate, documentsToUpdate, documentsToDelete } = splitDocumentsForMutations(
    updateDocumentsPosition(editableDocuments.value),
  )
  const { screenshotsToCreate, screenshotsToUpdate, screenshotsToDelete } = splitScreenshotsForMutations(editableScreenshots.value)

  const documentsMutations: Promise<unknown>[] = []
  if (documentsToUpdate.length > 0) {
    documentsMutations.push(
      mutateUpdateDocuments({
        attachableId: props.globalId,
        documentAttributes: documentsToUpdate,
      }),
    )
  }
  if (documentsToCreate.length > 0) {
    documentsMutations.push(
      ...documentsToCreate.map((document) =>
        mutateCreateDocument({
          attachableId: props.globalId,
          documentAttributes: document,
        }),
      ),
    )
  }
  if (documentsToDelete.length > 0) {
    documentsMutations.push(
      mutateDeleteDocuments({
        attachableId: props.globalId,
        ids: documentsToDelete,
      }),
    )
  }

  const screenshotsMutations: Promise<unknown>[] = []
  if (screenshotsToUpdate.length > 0) {
    screenshotsMutations.push(
      mutateUpdateScreenshots({
        attachableId: props.globalId,
        screenshotAttributes: screenshotsToUpdate,
      }),
    )
  }
  if (screenshotsToCreate.length > 0) {
    screenshotsMutations.push(
      ...screenshotsToCreate.map((screenshot) =>
        mutateCreateScreenshot({
          attachableId: props.globalId,
          screenshotAttributes: screenshot,
        }),
      ),
    )
  }
  if (screenshotsToDelete.length > 0) {
    screenshotsMutations.push(
      mutateDeleteScreenshots({
        attachableId: props.globalId,
        ids: screenshotsToDelete,
      }),
    )
  }

  try {
    await Promise.all([...documentsMutations, ...screenshotsMutations])
    uploadProgress.value = 0
    emit('update')
  } catch (_e) {
    uploadProgress.value = 0
  }
}

onErrorUpdateDocuments((error) => showError(error))
onErrorCreateDocument((error) => showError(error))
onErrorDeleteDocuments((error) => showError(error))
onErrorUpdateScreenshots((error) => showError(error))
onErrorCreateScreenshot((error) => showError(error))
onErrorDeleteScreenshots((error) => showError(error))

const showError = (error: Error) => {
  uploadProgress.value = 0
  if (error) showNotification(error.message, 'error')
}
</script>
